/**
 * The Overview grid (§2.11 Grid; screenshots/67–70 view mode, 68 customize, 74 interactions).
 * 12 columns · 80px · 16px gaps · 72px rows inside the 1136px content area; columns narrow when the library drawer
 * pushes the page (cells stay cells, so S/M/L never change because of the drawer). Positions come from the engine's
 * cellsToPx, and the layout (including the live drag preview) from the grid's layout store.
 * Widgets sit at translate(left, top); a move or reflow is a CSS transform transition (gridReflow 220ms, i.e. FLIP),
 * which never runs on first paint. Customize: column tint (customizeEnter), selection handles + drag pill + size tag,
 * the drop placeholder, and library tiles dropped onto cells.
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import type { UserGroup } from "../nav/navConfig";
import { entryOf } from "./catalog";
import { COLS, COL_PX, GAP_PX as GAP, ROW_STEP, addItem, cellsToPx, clampItem, layoutHeight, sizeTag } from "./grid/layoutEngine";
import { OvIcon } from "./icons";
import { overviewStore, useOverview, useOverviewLayout } from "./overviewStore";
import type { LayoutItem, WidgetType } from "./types";
import { useGridDrag, type Dir } from "./useGridDrag";
import { WidgetFrame } from "./WidgetFrame";

export const DRAG_TYPE = "application/x-overview-widget";
const DIRS: Dir[] = ["nw", "ne", "sw", "se", "e", "s"];
const NEW_ID = "__new";

type Box = Pick<LayoutItem, "x" | "y" | "w" | "h">;

/** `fluid` (Analytics dashboards, screenshots/84 at 1840px): columns grow past 80px to fill the page; cells stay cells. */
export function Grid({ group, fluid = false }: { group: UserGroup; fluid?: boolean }) {
  const { selected, removing, feeds, snapshot } = useOverview(useShallow((s) => ({
    selected: s.selected, removing: s.removing, feeds: s.feeds, snapshot: s.snapshot })));
  const { items, customizing, picked, message } = useOverviewLayout(useShallow((s) => ({
    items: s.layout, customizing: s.customizing, picked: s.picked, message: s.message })));
  const host = useRef<HTMLDivElement>(null);
  const [colW, setColW] = useState(COL_PX);
  // A library tile being dragged over the grid: the placeholder snaps to the cells under the pointer (68, 74 #2).
  const [incoming, setIncoming] = useState<{ type: WidgetType; box: Box } | null>(null);

  useLayoutEffect(() => {
    const el = host.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setColW(Math.max(24, Math.min(fluid ? Infinity : COL_PX, (e.contentRect.width - (COLS - 1) * GAP) / COLS))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [fluid]);

  const { lifted, onCardPointerDown, onHandlePointerDown, onCardKeyDown } = useGridDrag(host, colW);
  const layout = useMemo(() => (incoming ? addItem(items, { id: NEW_ID, type: incoming.type, ...incoming.box, config: {} }) : items), [items, incoming]);
  const height = Math.max(1, layoutHeight(layout) + (customizing ? 2 : 0));

  const cellAt = useCallback((clientX: number, clientY: number, w: number, h: number) => {
    const r = host.current!.getBoundingClientRect();
    return clampItem({ x: Math.round((clientX - r.left) / (colW + GAP) - w / 2), y: Math.round((clientY - r.top) / ROW_STEP - h / 2), w, h });
  }, [colW]);
  const dragType = (e: React.DragEvent) => e.dataTransfer.types.includes(DRAG_TYPE);
  const onDragOver = (e: React.DragEvent) => {
    if (!customizing || !dragType(e)) return;
    e.preventDefault();
    const type = (document.body.dataset.ovDrag ?? "") as WidgetType;
    if (!type) return;
    const { w, h } = entryOf(type).defaultSize;
    const box = cellAt(e.clientX, e.clientY, w, h);
    if (!incoming || incoming.box.x !== box.x || incoming.box.y !== box.y) setIncoming({ type, box });
  };
  const onDrop = (e: React.DragEvent) => {
    const type = e.dataTransfer.getData(DRAG_TYPE) as WidgetType;
    if (!customizing || !type) return;
    e.preventDefault();
    const { w, h } = entryOf(type).defaultSize;
    const box = incoming?.box ?? cellAt(e.clientX, e.clientY, w, h);
    setIncoming(null);
    overviewStore.getState().addWidget(type, { x: box.x, y: box.y });
  };

  const px = (b: Box) => cellsToPx(b, colW);
  const ghost = incoming ? layout.find((i) => i.id === NEW_ID) : lifted ? layout.find((i) => i.id === lifted.id) : null;

  return (
    <div ref={host} className={`ov-grid${customizing ? " is-customizing" : ""}`} style={{ height: height * ROW_STEP - GAP }}
      onDragOver={onDragOver} onDrop={onDrop} onDragLeave={(e) => { if (!host.current?.contains(e.relatedTarget as Node)) setIncoming(null); }}
      onPointerDown={(e) => { if (customizing && e.target === e.currentTarget) overviewStore.getState().select(null); }}>
      {customizing && <div className="ov-grid__cols" aria-hidden>{Array.from({ length: COLS }, (_, i) => <span key={i} style={{ left: i * (colW + GAP), width: colW }} />)}</div>}
      {ghost && <div className="ov-grid__placeholder" aria-hidden style={{ ...pos(px(ghost)) }} />}
      {layout.filter((i) => i.id !== NEW_ID).map((it) => {
        // The card being dragged stays at its starting cells (offset by the pointer); the placeholder shows where it lands.
        const at = lifted?.mode === "drag" && lifted.id === it.id ? { ...it, ...lifted.origin } : it;
        const isSel = customizing && selected === it.id;
        const resizing = lifted?.mode === "resize" && lifted.id === it.id;
        return (
          <WidgetFrame key={it.id} item={it} colW={colW} group={group} feeds={feeds} snapshot={snapshot}
            customizing={customizing} selected={isSel} removing={removing === it.id}
            className={lifted?.mode === "drag" && lifted.id === it.id ? "is-lifted" : picked === it.id ? "is-picked" : undefined}
            style={pos(px(at))} tabIndex={customizing ? 0 : undefined}
            aria-roledescription={customizing ? "Draggable widget" : undefined}
            onPointerDown={customizing ? onCardPointerDown(it) : undefined}
            onKeyDown={customizing ? onCardKeyDown(it) : undefined}
            onFocus={customizing ? () => overviewStore.getState().select(it.id) : undefined}
            onRemove={() => overviewStore.getState().removeWidget(it.id)}
            chrome={isSel ? (
              <>
                <span className="ov-pill" aria-hidden><OvIcon name="grip-vertical" size={12} /></span>
                {DIRS.map((d) => <span key={d} data-handle={d} className={`ov-handle ov-handle--${d}`} aria-hidden onPointerDown={onHandlePointerDown(it, d)} />)}
                <span className="ov-sizetag" aria-hidden>{sizeTag(it.w, it.h, resizing)}</span>
              </>
            ) : undefined} />
        );
      })}
      <div className="ov-sr" role="status" aria-live="polite">{message}</div>
    </div>
  );
}

const pos = (p: { left: number; top: number; width: number; height: number }): React.CSSProperties =>
  ({ transform: `translate(${p.left}px, ${p.top}px)`, width: p.width, height: p.height });
